import { onMounted, onUnmounted } from 'vue';

interface ShortcutOptions {
  /** The key to bind, e.g. 'n', '/', 'Escape'. Case-sensitive per KeyboardEvent.key */
  key: string;
  /** Require Ctrl (Cmd on Mac) to be held */
  ctrl?: boolean;
  /** Require Meta (Cmd on Mac, Windows key on Win) */
  meta?: boolean;
  /** Require Shift */
  shift?: boolean;
  /**
   * If true, fire the handler even when focus is inside an input / textarea /
   * contenteditable. Defaults to false so a user typing into a form never
   * accidentally triggers a global shortcut.
   */
  allowInInput?: boolean;
  /**
   * Allow the shortcut to fire on either Ctrl OR Meta (macOS-friendly).
   * Equivalent to `ctrl: true` plus a check for metaKey. Useful for Cmd+K
   * style bindings that should work on both platforms.
   */
  ctrlOrMeta?: boolean;
  handler: (event: KeyboardEvent) => void;
}

/**
 * Registers a document-level keyboard shortcut for the lifetime of the
 * calling component. Cleans up on unmount.
 *
 * The default is to skip the handler when focus is inside an editable element
 * — pressing 'n' while typing a threat actor name should not open the quick-
 * add overlay. Set `allowInInput: true` to override (e.g. for Ctrl+Enter
 * inside a form).
 *
 * Example:
 *   useKeyboardShortcut({
 *     key: 'n',
 *     handler: () => quickAddStore.openPicker(),
 *   });
 *
 *   useKeyboardShortcut({
 *     key: 'Enter',
 *     ctrlOrMeta: true,
 *     allowInInput: true,
 *     handler: () => save(),
 *   });
 */
export function useKeyboardShortcut(options: ShortcutOptions) {
  function onKey(event: KeyboardEvent) {
    if (event.key !== options.key) return;

    if (options.ctrlOrMeta) {
      if (!event.ctrlKey && !event.metaKey) return;
    } else {
      if (options.ctrl && !event.ctrlKey) return;
      if (options.meta && !event.metaKey) return;
    }
    if (options.shift && !event.shiftKey) return;

    if (!options.allowInInput) {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
    }

    options.handler(event);
  }

  onMounted(() => document.addEventListener('keydown', onKey));
  onUnmounted(() => document.removeEventListener('keydown', onKey));
}
